/**
 * Preload All LOD Levels Hook
 * Preloads all texture sets in the background to prevent loading delays during LOD transitions
 * Creates Data3DTexture arrays and uploads to GPU memory for immediate availability
 */

import { useEffect, useRef, useState } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { getTilePaths, TILE_GRID } from '../textures/tileConfig'
import { setCachedTextureArray, hasTextureArray, getCacheStats } from '../textures/textureCache'

// All texture types to preload. The material applies normal/specular only
// `if (array)` (see earthTiledMaterial.js) so omitting them is safe — they're
// dropped on mobile to cut ~40% of Earth tile GPU memory.
const ALL_TEXTURE_TYPES = ['diffuse', 'normal', 'specular', 'nightmap', 'clouds']

/**
 * Create a Data3DTexture array from loaded tiles
 */
async function createTextureArrayFromPaths(paths, columns, rows, textureType, gl) {
  const textureLoader = new THREE.TextureLoader()
  
  // Load all tiles
  const loadedTextures = await Promise.all(
    paths.map(path => 
      new Promise((resolve, reject) => {
        textureLoader.load(
          path,
          (tex) => {
            // Configure texture
            if (textureType === 'diffuse' || textureType === 'nightmap' || textureType === 'clouds') {
              tex.colorSpace = THREE.SRGBColorSpace
            } else {
              tex.colorSpace = THREE.LinearSRGBColorSpace
            }
            tex.generateMipmaps = false
            tex.minFilter = THREE.LinearFilter
            tex.magFilter = THREE.LinearFilter
            resolve(tex)
          },
          undefined,
          reject
        )
      })
    )
  )

  // Create Data3DTexture array
  const firstTile = loadedTextures[0]
  if (!firstTile.image) return null

  const tileSize = firstTile.image.width
  const depth = loadedTextures.length
  const data = new Uint8Array(tileSize * tileSize * depth * 4)

  // Copy each tile into the array
  loadedTextures.forEach((tileTex, index) => {
    if (!tileTex.image) return

    const canvas = document.createElement('canvas')
    canvas.width = tileSize
    canvas.height = tileSize
    let ctx = canvas.getContext('2d', { willReadFrequently: true })
    
    ctx.clearRect(0, 0, tileSize, tileSize)
    ctx.drawImage(tileTex.image, 0, 0, tileSize, tileSize)
    
    const imageData = ctx.getImageData(0, 0, tileSize, tileSize)
    const offset = index * tileSize * tileSize * 4
    data.set(imageData.data, offset)

    // CRITICAL: Proper GPU memory cleanup
    tileTex.dispose()
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    // Force GPU context flush
    try {
      ctx.getImageData(0, 0, 1, 1)
    } catch (e) { }
    // Release context reference
    ctx = null
    canvas.width = 0
    canvas.height = 0
    canvas.remove()
  })

  // Create the Data3DTexture
  const texture3D = new THREE.Data3DTexture(data, tileSize, tileSize, depth)
  texture3D.format = THREE.RGBAFormat
  texture3D.type = THREE.UnsignedByteType
  
  // Set color space based on texture type
  if (textureType === 'diffuse' || textureType === 'nightmap' || textureType === 'clouds') {
    texture3D.colorSpace = THREE.SRGBColorSpace
  } else {
    texture3D.colorSpace = THREE.LinearSRGBColorSpace
  }
  
  texture3D.minFilter = THREE.LinearFilter
  texture3D.magFilter = THREE.LinearFilter
  texture3D.wrapS = THREE.ClampToEdgeWrapping
  texture3D.wrapT = THREE.ClampToEdgeWrapping
  texture3D.wrapR = THREE.ClampToEdgeWrapping
  texture3D.anisotropy = 16 // Max anisotropic filtering for quality
  texture3D.unpackAlignment = 1
  texture3D.needsUpdate = true

  // Force GPU upload by rendering with it
  if (gl.initTexture) {
    gl.initTexture(texture3D)
    // Release the CPU-side typed array once it's on the GPU. These arrays are
    // static (never re-uploaded), so keeping texture3D.image.data around just
    // doubles the texture's memory footprint (CPU copy + GPU copy). Freeing it
    // roughly halves tile RAM — important on memory-constrained mobile (iOS).
    if (texture3D.image) {
      texture3D.image.data = null
    }
  }

  return texture3D
}

/**
 * Preload all LOD levels and all texture types in the background
 * Creates Data3DTexture arrays and uploads to GPU memory
 * @param {Array<string>} lodLevels - Array of LOD level names to preload (e.g., ['LOD_0', 'LOD_1', ...])
 * @param {boolean} enabled - Whether preloading is enabled
 */
export function usePreloadAllLODs(lodLevels = [], enabled = true, textureTypes = ALL_TEXTURE_TYPES) {
  const { gl } = useThree()
  const [preloadedCount, setPreloadedCount] = useState(0)
  const [currentLoadingItem, setCurrentLoadingItem] = useState('')
  const isPreloadingRef = useRef(false)

  useEffect(() => {
    if (!enabled || isPreloadingRef.current || lodLevels.length === 0) {
      return
    }

    isPreloadingRef.current = true

    const preloadSequentially = async () => {
      let totalLoaded = 0
      const totalToLoad = lodLevels.length * textureTypes.length

      for (const lodLevel of lodLevels) {
        const gridConfig = TILE_GRID[lodLevel]
        if (!gridConfig) continue

        for (const textureType of textureTypes) {
          setCurrentLoadingItem(`Earth ${lodLevel} ${textureType}`)
          
          // Check if already cached
          if (hasTextureArray(lodLevel, textureType)) {
            totalLoaded++
            setPreloadedCount(totalLoaded)
            continue
          }

          try {
            const paths = getTilePaths(lodLevel, textureType)
            
            // Create Data3DTexture array from tiles
            const texture3D = await createTextureArrayFromPaths(
              paths,
              gridConfig.columns,
              gridConfig.rows,
              textureType,
              gl
            )

            if (texture3D) {
              // Store in shared cache
              setCachedTextureArray(lodLevel, textureType, texture3D)
              totalLoaded++
              setPreloadedCount(totalLoaded)
            }
          } catch (error) {
            // Silent error handling
          }
        }
      }

      setCurrentLoadingItem('')
      isPreloadingRef.current = false
    }

    // Start preloading immediately
    preloadSequentially()

    return () => {
      // Note: We don't dispose textures here as they're in the shared cache
      // and may be used by other components
    }
  }, [lodLevels, enabled, textureTypes])

  return {
    isPreloading: isPreloadingRef.current,
    preloadedCount: preloadedCount,
    currentLoadingItem: currentLoadingItem
  }
}